import React, { useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { UserCog, KeyRound, Save, CheckCircle2, Loader2 } from "lucide-react";

export default function UserSettings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    company_name: user?.company_name || "",
    current_password: "",
  });
  const emailChanged = (form.email || "").trim().toLowerCase() !== (user?.email || "").toLowerCase();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const [savingP, setSavingP] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [errP, setErrP] = useState("");
  const [errPw, setErrPw] = useState("");
  const [savedP, setSavedP] = useState(false);
  const [savedPw, setSavedPw] = useState(false);

  const onF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onP = (k) => (e) => setPw({ ...pw, [k]: e.target.value });

  const saveProfile = async (e) => {
    e.preventDefault();
    setErrP(""); setSavedP(false); setSavingP(true);
    try {
      const body = { name: form.name, email: form.email, company_name: form.company_name };
      if (emailChanged) body.current_password = form.current_password;
      await api.put("/auth/me", body);
      await refreshUser();
      setForm((f) => ({ ...f, current_password: "" }));
      setSavedP(true);
      setTimeout(() => setSavedP(false), 2500);
    } catch (e) { setErrP(formatErr(e)); }
    finally { setSavingP(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setErrPw(""); setSavedPw(false);
    if (pw.new_password !== pw.confirm_password) { setErrPw("Die beiden neuen Passwörter stimmen nicht überein."); return; }
    if ((pw.new_password || "").length < 10) { setErrPw("Neues Passwort muss mindestens 10 Zeichen haben."); return; }
    setSavingPw(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      // Changing the password ends all sessions; keep this one with the new token.
      if (data?.token) localStorage.setItem("auth_token", data.token);
      setPw({ current_password: "", new_password: "", confirm_password: "" });
      setSavedPw(true);
      setTimeout(() => setSavedPw(false), 2500);
    } catch (e) { setErrPw(formatErr(e)); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="space-y-6" data-testid="user-settings-section">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <UserCog size={22} className="text-amber-300"/>
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold">Benutzerkonto</h2>
          <p className="text-slate-400 text-sm mt-1">Name, Login-E-Mail und Passwort anpassen.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={saveProfile} className="card p-6 space-y-4" data-testid="user-profile-form">
          <div className="font-display font-semibold text-slate-100 mb-1">Persönliche Daten</div>
          <div>
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={onF("name")} data-testid="user-name-input"/>
          </div>
          <div>
            <label className="label">Login-E-Mail</label>
            <input type="email" className="input" value={form.email} onChange={onF("email")} data-testid="user-email-input"/>
          </div>
          {emailChanged && (
            <div>
              <label className="label">Aktuelles Passwort (zum Ändern der E-Mail)</label>
              <input type="password" className="input" value={form.current_password} onChange={onF("current_password")} required autoComplete="current-password" data-testid="user-email-password-input"/>
            </div>
          )}
          <div>
            <label className="label">Firma</label>
            <input className="input" value={form.company_name} onChange={onF("company_name")} data-testid="user-company-input"/>
          </div>
          {errP && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3" data-testid="user-profile-error">{errP}</div>}
          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-800">
            {savedP && <span className="chip chip-valid" data-testid="user-profile-saved"><CheckCircle2 size={12}/>Gespeichert</span>}
            <button className="btn-primary" type="submit" disabled={savingP} data-testid="user-save-button">
              {savingP ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>}
              {savingP ? "Speichere..." : "Daten speichern"}
            </button>
          </div>
        </form>

        <form onSubmit={changePw} className="card p-6 space-y-4" data-testid="user-password-form">
          <div className="flex items-center gap-2 font-display font-semibold text-slate-100 mb-1">
            <KeyRound size={16} className="text-slate-400"/>Passwort ändern
          </div>
          <div>
            <label className="label">Aktuelles Passwort</label>
            <input type="password" className="input" value={pw.current_password} onChange={onP("current_password")} data-testid="current-password-input" autoComplete="current-password"/>
          </div>
          <div>
            <label className="label">Neues Passwort</label>
            <input type="password" className="input" value={pw.new_password} onChange={onP("new_password")} data-testid="new-password-input" autoComplete="new-password" placeholder="min. 10 Zeichen"/>
          </div>
          <div>
            <label className="label">Neues Passwort bestätigen</label>
            <input type="password" className="input" value={pw.confirm_password} onChange={onP("confirm_password")} data-testid="confirm-password-input" autoComplete="new-password"/>
          </div>
          {errPw && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3" data-testid="user-password-error">{errPw}</div>}
          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-800">
            {savedPw && <span className="chip chip-valid" data-testid="user-password-saved"><CheckCircle2 size={12}/>Passwort geändert</span>}
            <button className="btn-primary" type="submit" disabled={savingPw} data-testid="change-password-button">
              {savingPw ? <Loader2 size={16} className="animate-spin"/> : <KeyRound size={16}/>}
              {savingPw ? "Ändere..." : "Passwort ändern"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
